import _ from 'lodash';
import { useCallback, useState } from 'react';

type SetStateAction<S> = Partial<S> | ((prevState: S) => Partial<S> | null);

/**
 * Provides class-style partial updates for a single state object. Wraps useState with a memoised setter that merges a patch object, or the result of a patch function, into the previous state through lodash assign and keeps the previous state when a patch function returns nothing.
 *
 * @param initialState - Initial value of the state object.
 * @returns Tuple of the current state and the merging setter.
 */
const useSetState = <S extends object>(
  initialState: S,
): [S, (patch: SetStateAction<S>) => void] => {
  const [state, set] = useState<S>(initialState);
  /**
   * Merges a partial update into the hook's state object. Applies a patch function against the previous state and ignores a nil result, otherwise shallow-merges the patch with lodash assign, staying referentially stable through useCallback.
   *
   * @param patch - Partial state, or a function producing it from the previous state.
   * @returns Nothing.
   */
  const setState = useCallback((patch: SetStateAction<S>) => {
    set((prevState) => {
      if (_.isFunction(patch)) {
        const data = patch(prevState);
        if (_.isNil(data)) {
          return prevState;
        }
        return _.assign({}, prevState, data);
      }
      return _.assign({}, prevState, patch);
    });
  }, []);
  return [state, setState];
};

export default useSetState;
